import { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Customized,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { CategoricalChartState } from "recharts/types/chart/types";
import type { EarningsOut, PriceSeriesOut } from "@/api/client";

const EARNINGS_COLOR = "hsl(38 92% 55%)";

export type ChartRange = "1W" | "1M" | "3M" | "1Y" | "5Y" | "ALL";

const RANGE_DAYS: Record<ChartRange, number | null> = {
  "1W": 7,
  "1M": 30,
  "3M": 90,
  "1Y": 365,
  "5Y": 365 * 5,
  ALL: null,
};

interface ChartPoint {
  date: string;
  price: number;
}

export function StockChart({
  series,
  range,
  lockedDate,
  onLockedChange,
  earnings,
  height = 360,
}: {
  series: PriceSeriesOut | undefined;
  range: ChartRange;
  lockedDate: string | null;
  onLockedChange: (d: string | null) => void;
  earnings?: EarningsOut[];
  height?: number;
}) {
  const [hoverDate, setHoverDate] = useState<string | null>(null);

  const data = useMemo<ChartPoint[]>(() => {
    if (!series) return [];
    const all = series.points.map((p) => ({ date: p.date, price: Number(p.close) }));
    const cutoff = RANGE_DAYS[range];
    return cutoff === null ? all : all.slice(-cutoff);
  }, [series, range]);

  const earningsMarkers = useMemo<EarningsMarker[]>(() => {
    if (!earnings || data.length === 0) return [];
    const dateToIndex = new Map<string, number>();
    data.forEach((p, i) => dateToIndex.set(p.date, i));
    const out: EarningsMarker[] = [];
    for (const e of earnings) {
      const i = dateToIndex.get(e.announcement_date);
      if (i === undefined) continue;
      out.push({
        date: e.announcement_date,
        price: data[i].price,
        priceBefore: i > 0 ? data[i - 1].price : null,
        priceAfter: i < data.length - 1 ? data[i + 1].price : null,
      });
    }
    return out;
  }, [earnings, data]);

  const earningsByDate = useMemo(() => {
    const m = new Map<string, EarningsMarker>();
    for (const e of earningsMarkers) m.set(e.date, e);
    return m;
  }, [earningsMarkers]);

  useEffect(() => {
    if (!lockedDate) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const idx = data.findIndex((p) => p.date === lockedDate);
      if (idx === -1) return;
      const next = e.key === "ArrowLeft" ? idx - 1 : idx + 1;
      if (next < 0 || next >= data.length) return;
      e.preventDefault();
      onLockedChange(data[next].date);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lockedDate, data, onLockedChange]);

  if (!series) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center text-[12px] text-muted-foreground"
      >
        Loading chart…
      </div>
    );
  }
  if (data.length === 0) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center text-[12px] text-muted-foreground"
      >
        No price data available for this range.
      </div>
    );
  }

  const first = data[0]?.price ?? 0;
  const last = data[data.length - 1]?.price ?? 0;
  const change = last - first;
  const changePct = first ? (change / first) * 100 : 0;
  const up = change >= 0;
  const lineColor = up ? "hsl(var(--chart-up))" : "hsl(var(--chart-down))";

  const activeDate = lockedDate ?? hoverDate;

  function pickDate(state: CategoricalChartState | undefined): string | null {
    if (!state || !state.activePayload || state.activePayload.length === 0) return null;
    const payload = state.activePayload[0]?.payload as ChartPoint | undefined;
    return payload?.date ?? null;
  }

  return (
    <div className="w-full select-none">
      <div className="flex items-baseline justify-between mb-1">
        <div className="flex items-baseline gap-2">
          <div className="text-[11px] text-muted-foreground font-mono uppercase tracking-wider">
            {range} change
          </div>
          <div
            className="text-[13px] font-mono font-semibold"
            style={{ color: lineColor }}
          >
            {up ? "+" : ""}
            {change.toFixed(2)} ({up ? "+" : ""}
            {changePct.toFixed(2)}%)
          </div>
        </div>
      </div>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 12, right: 14, bottom: 4, left: 8 }}
            onMouseMove={(state) => setHoverDate(pickDate(state))}
            onMouseLeave={() => setHoverDate(null)}
            onClick={(state) => {
              const d = pickDate(state);
              if (!d) return;
              onLockedChange(lockedDate === d ? null : d);
            }}
            style={{ cursor: lockedDate ? "default" : "crosshair" }}
          >
            <defs>
              <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={lineColor} stopOpacity={0.22} />
                <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="earningsLineCustom" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={EARNINGS_COLOR} stopOpacity={1} />
                <stop offset="50%" stopColor={EARNINGS_COLOR} stopOpacity={0.65} />
                <stop offset="100%" stopColor={EARNINGS_COLOR} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="2 3" stroke="hsl(var(--border))" opacity={0.5} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 9, fontFamily: "ui-monospace, monospace", fill: "hsl(var(--muted-foreground))" }}
              minTickGap={32}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={["auto", "auto"]}
              tick={{ fontSize: 9, fontFamily: "ui-monospace, monospace", fill: "hsl(var(--muted-foreground))" }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip
              cursor={false}
              wrapperStyle={{ outline: "none" }}
              content={<ChartTooltip earningsByDate={earningsByDate} />}
            />
            <Area
              type="monotone"
              dataKey="price"
              stroke={lineColor}
              strokeWidth={1.4}
              fill="url(#chartFill)"
              isAnimationActive={false}
              activeDot={{ r: 3, stroke: lineColor, strokeWidth: 1.5, fill: "hsl(var(--background))" }}
            />
            <Customized
              component={(props: unknown) => renderEarningsMarkers(props, earningsMarkers)}
            />

            {activeDate && (
              <ReferenceLine
                x={activeDate}
                stroke={lockedDate ? "hsl(var(--primary))" : "hsl(var(--foreground))"}
                strokeDasharray={lockedDate ? undefined : "3 3"}
                strokeOpacity={lockedDate ? 0.9 : 0.6}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[10px] text-muted-foreground mt-1 font-mono">
        {lockedDate ? (
          <>
            Locked on <span className="text-foreground">{lockedDate}</span> — click again to
            unlock.
          </>
        ) : (
          <>Hover chart to inspect. Click a point to lock a date and see articles.</>
        )}
      </div>
    </div>
  );
}

interface EarningsMarker {
  date: string;
  price: number;
  priceBefore: number | null;
  priceAfter: number | null;
}

interface CustomizedProps {
  xAxisMap?: Record<string, { scale: (v: string) => number }>;
  yAxisMap?: Record<string, { scale: (v: number) => number }>;
  offset?: { top: number; bottom: number; height: number };
}

const UP_COLOR = "hsl(var(--chart-up))";
const DOWN_COLOR = "hsl(var(--chart-down))";

function renderEarningsMarkers(props: unknown, markers: EarningsMarker[]): React.ReactNode {
  const p = props as CustomizedProps;
  const xAxis = p.xAxisMap ? Object.values(p.xAxisMap)[0] : undefined;
  const yAxis = p.yAxisMap ? Object.values(p.yAxisMap)[0] : undefined;
  const offset = p.offset;
  if (!xAxis || !yAxis || !offset) return null;

  const bottomY = offset.top + offset.height;
  return (
    <g>
      {markers.map((m) => {
        const cx = xAxis.scale(m.date);
        const cy = yAxis.scale(m.price);
        if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
        return (
          <g key={`earnings-${m.date}`}>
            <rect
              x={cx - 1.5}
              y={cy}
              width={3}
              height={Math.max(0, bottomY - cy)}
              fill="url(#earningsLineCustom)"
            />
            <circle
              cx={cx}
              cy={cy}
              r={4.5}
              fill={EARNINGS_COLOR}
              stroke="hsl(var(--background))"
              strokeWidth={1.5}
            />
          </g>
        );
      })}
    </g>
  );
}

interface TooltipPayloadItem {
  payload?: ChartPoint;
  value?: number;
}

function ChartTooltip({
  active,
  payload,
  label,
  earningsByDate,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  earningsByDate: Map<string, EarningsMarker>;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  const wrapperStyle: React.CSSProperties = {
    background: "hsl(var(--popover))",
    border: "1px solid hsl(var(--border))",
    borderRadius: 4,
    padding: "4px 8px",
    fontSize: 10,
    fontFamily: "ui-monospace, monospace",
    color: "hsl(var(--foreground))",
  };

  const earnings = earningsByDate.get(point.date);
  if (!earnings) {
    return (
      <div style={wrapperStyle}>
        <div style={{ color: "hsl(var(--muted-foreground))" }}>{label}</div>
        <div>${point.price.toFixed(2)}</div>
      </div>
    );
  }

  const rows: { label: string; price: number | null; pctRef: number | null }[] = [
    { label: "Day before", price: earnings.priceBefore, pctRef: null },
    { label: "Announcement", price: earnings.price, pctRef: earnings.priceBefore },
    { label: "Day after", price: earnings.priceAfter, pctRef: earnings.price },
  ];
  return (
    <div style={wrapperStyle}>
      <div style={{ color: EARNINGS_COLOR, marginBottom: 2 }}>Earnings — {label}</div>
      {rows.map((r) => {
        const priceText = r.price !== null ? `$${r.price.toFixed(2)}` : "—";
        let pctText = "";
        let pctColor = "hsl(var(--muted-foreground))";
        if (r.price !== null && r.pctRef !== null && r.pctRef !== 0) {
          const pct = ((r.price - r.pctRef) / r.pctRef) * 100;
          const sign = pct >= 0 ? "+" : "";
          pctText = ` (${sign}${pct.toFixed(2)}%)`;
          pctColor = pct >= 0 ? UP_COLOR : DOWN_COLOR;
        }
        return (
          <div
            key={r.label}
            style={{ display: "flex", justifyContent: "space-between", gap: 12 }}
          >
            <span style={{ color: "hsl(var(--muted-foreground))" }}>{r.label}</span>
            <span>
              {priceText}
              <span style={{ color: pctColor }}>{pctText}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
